import {
  DEFAULT_LANGUAGE,
  LOCAL_STORAGE_KEY,
  PREFERRED_LANGUAGE_DEAL_SYNC_COMPLETED_KEY,
  PREFERRED_LANGUAGE_USER_SELECTED_KEY,
  SUPPORTED_LANGUAGES,
} from "utilities/Constant";
import type { MyLoanInfo } from "pages/application/applicationService";

export function selectActiveLoan(
  loans: MyLoanInfo[] | undefined,
  nhDealApplicationId?: string | null,
): MyLoanInfo | undefined {
  if (!loans?.length || !nhDealApplicationId?.trim()) {
    return undefined;
  }

  return loans.find((loan) => loan.nhDealApplicationId === nhDealApplicationId);
}

export type ResolveDisplayedLanguageOptions = {
  isExplicitSelection: boolean;
  isMyLoansPage: boolean;
  hasApplicationContext: boolean;
  isPostLosActiveLoan: boolean;
  userDbLanguage?: string | null;
  activeLoan?: Pick<MyLoanInfo, "dealPreferredLanguage" | "loanNumber"> | null;
  reduxLanguage?: string | null;
};

export function resolveDisplayedPreferredLanguage(options: ResolveDisplayedLanguageOptions): string {
  const {
    isExplicitSelection,
    isMyLoansPage,
    hasApplicationContext,
    isPostLosActiveLoan,
    userDbLanguage,
    activeLoan,
    reduxLanguage,
  } = options;

  const inApplication = !isMyLoansPage && hasApplicationContext && Boolean(activeLoan);

  // Post-LOS application: always the deal language, even after a user toggle elsewhere.
  if (inApplication && isPostLosActiveLoan) {
    return normalizeToAppLanguage(activeLoan?.dealPreferredLanguage);
  }

  if (isExplicitSelection) {
    return getPreferredLanguage(reduxLanguage);
  }

  if (inApplication) {
    return normalizeToAppLanguage(activeLoan?.dealPreferredLanguage);
  }

  if (isMyLoansPage) {
    if (userDbLanguage?.trim()) {
      return normalizeToAppLanguage(userDbLanguage);
    }

    return getPreferredLanguage(reduxLanguage);
  }

  return getPreferredLanguage(reduxLanguage);
}

export function resolveLanguageForLoanEntry(
  loan: Pick<MyLoanInfo, "dealPreferredLanguage" | "loanNumber">,
  reduxLanguage?: string | null,
): string {
  return resolveDisplayedPreferredLanguage({
    isExplicitSelection: hasExplicitUserLanguageSelection(),
    isMyLoansPage: false,
    hasApplicationContext: true,
    isPostLosActiveLoan: Boolean(loan.loanNumber && loan.loanNumber !== "TBD"),
    activeLoan: loan,
    reduxLanguage,
  });
}

export function resolveLanguageForMyLoansLanding(
  userDbLanguage?: string | null,
  reduxLanguage?: string | null,
): string {
  return resolveDisplayedPreferredLanguage({
    isExplicitSelection: hasExplicitUserLanguageSelection(),
    isMyLoansPage: true,
    hasApplicationContext: false,
    isPostLosActiveLoan: false,
    userDbLanguage,
    reduxLanguage,
  });
}

export function shouldPersistLoanEntryUserLanguage(
  resolvedLanguage: string,
  reduxLanguage?: string | null,
): boolean {
  if (!hasExplicitUserLanguageSelection()) {
    return true;
  }

  return normalizeToAppLanguage(resolvedLanguage) === getPreferredLanguage(reduxLanguage);
}

export function getPersistedPreferredLanguage(): string | null {
  const saved = globalThis.sessionStorage.getItem(LOCAL_STORAGE_KEY);
  return saved?.trim() ? normalizeToAppLanguage(saved) : null;
}

export function setPersistedPreferredLanguage(value: string): void {
  globalThis.sessionStorage.setItem(LOCAL_STORAGE_KEY, normalizeToAppLanguage(value));
}

export function normalizeToAppLanguage(value?: string | null): string {
  if (!value?.trim()) {
    return DEFAULT_LANGUAGE;
  }

  switch (value.trim().toLowerCase()) {
    case "english":
      return "English";
    case "spanish":
    case "español":
    case "espanol":
      return "Español";
    default:
      return SUPPORTED_LANGUAGES.includes(value) ? value : DEFAULT_LANGUAGE;
  }
}

export function toUserDbPreferredLanguage(appLanguage: string): string {
  return normalizeToAppLanguage(appLanguage) === "Español" ? "Spanish" : "English";
}

export function getPreferredLanguage(reduxLanguage?: string | null): string {
  return normalizeToAppLanguage(
    getPersistedPreferredLanguage() ?? reduxLanguage ?? DEFAULT_LANGUAGE,
  );
}

export function isSpanishLanguage(value?: string | null): boolean {
  return normalizeToAppLanguage(value) === "Español";
}

export function hasExplicitUserLanguageSelection(): boolean {
  return globalThis.sessionStorage.getItem(PREFERRED_LANGUAGE_USER_SELECTED_KEY) === "true";
}

export function markExplicitUserLanguageSelection(): void {
  globalThis.sessionStorage.setItem(PREFERRED_LANGUAGE_USER_SELECTED_KEY, "true");
}

export function getExplicitUserLanguage(): string | null {
  if (!hasExplicitUserLanguageSelection()) {
    return null;
  }

  return getPersistedPreferredLanguage();
}

export function hasPreferredLanguageDealSyncCompleted(): boolean {
  return globalThis.sessionStorage.getItem(PREFERRED_LANGUAGE_DEAL_SYNC_COMPLETED_KEY) === "true";
}

export function markPreferredLanguageDealSyncCompleted(): void {
  globalThis.sessionStorage.setItem(PREFERRED_LANGUAGE_DEAL_SYNC_COMPLETED_KEY, "true");
}

export function clearPreferredLanguageStorageOnLogout(): void {
  globalThis.sessionStorage.removeItem(LOCAL_STORAGE_KEY);
  globalThis.sessionStorage.removeItem(PREFERRED_LANGUAGE_USER_SELECTED_KEY);
  globalThis.sessionStorage.removeItem(PREFERRED_LANGUAGE_DEAL_SYNC_COMPLETED_KEY);
  globalThis.localStorage.removeItem(LOCAL_STORAGE_KEY);
}

export function buildSyncPreferredLanguageRequest(
  userId: string,
  appLanguage: string,
): { userId: string; preferredLanguage: string } {
  return {
    userId,
    preferredLanguage: toUserDbPreferredLanguage(appLanguage),
  };
}
